'use client'
import { Grid } from '@achieve/ascend'

import React, { useReducer, useEffect, useState, useRef, useContext } from 'react'
import useViewportSize from 'hooks/useViewportSize'
import { LayoutContext } from 'providers/LayoutProvider'
import { videoMap } from 'constants/videoMappings'
import { TYPES_SOURCES_VIDEOS } from 'utils/shared'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'

import { useTriggerEvents } from 'hooks/useTriggerEvents'

import { MediaImageStandard } from 'components/Contentful'
import { VideoFrame } from './VideoFrame'
import { PlayBtn } from 'components/Svgs/PlayBtn'
import { HiddenIframe } from './VideoSEO'
import dynamic from 'next/dynamic'
const AchieveSkeleton = dynamic(() => import('components/AchieveSkeleton/AchieveSkeleton'))

const reducerFunction = (state, action) => {
  switch (action.type) {
    case 'initVideo':
      return { ...state, ...action.payload }
    case 'calculatedClient':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

function VideoPlayer({
  content = {},
  maxWidth = false,
  frameClassName,
  frameEmbed = false,
  aspectRatio,
  trackOnPlay = {},
  extraPadding = 40,
  event,
  capWidth = false,
  priority = false,
}) {
  const { source, autoplay, altText, thumbnail } = content

  const { breakpointLetterSize, calculatedClient, screenWidth } = useViewportSize()

  // Read context directly so SSR and hydration both use the same initial breakpoint.
  // useViewportSize bypasses the context (measures window) on first client render, causing
  // the useState/useReducer initial values to differ from SSR → hydration mismatch.
  const { state: { viewportSize: ssrViewportSize } = {} } = useContext(LayoutContext)
  const ssrBreakpoint = ssrViewportSize?.breakpointLetterSize ?? 'xl'

  const isMobile = useViewportSmallerThan(BREAKPOINTS.md)
  const [heightAspectRadio, setHeightAspectRadio] = useState(
    videoMap[ssrBreakpoint].height - extraPadding
  )

  const { fireAndForget } = useTriggerEvents()

  const [state, dispatch] = useReducer(reducerFunction, {
    typeVideo: null,
    showVideo: false,
    clickToPlay: false,
    dimension: {
      height: videoMap[ssrBreakpoint].height,
      width: videoMap[ssrBreakpoint].width - extraPadding,
    },
  })
  // use 40 to fix videoplayer padding on about us page
  const mobileWidth = maxWidth ? screenWidth : screenWidth - extraPadding

  useEffect(() => {
    let videoHandler = false
    if (document.readyState !== 'loading') {
      start()
    } else {
      videoHandler = true
      document.addEventListener('DOMContentLoaded', start)
    }
    function start() {
      if (videoHandler) {
        document.removeEventListener('DOMContentLoaded', start)
      }
      dispatch({
        type: 'initVideo',
        payload: {
          showVideo: true,
        },
      })
    }
  }, [])

  useEffect(() => {
    setHeightAspectRadio(isMobile ? mobileWidth / aspectRatio : state.dimension.width / aspectRatio)
  }, [isMobile, mobileWidth, state.dimension.width, aspectRatio])

  useEffect(() => {
    if (calculatedClient) {
      dispatch({
        type: 'calculatedClient',
        payload: {
          dimension: {
            height:
              capWidth && videoMap[breakpointLetterSize].height > capWidth / aspectRatio
                ? capWidth / aspectRatio
                : videoMap[breakpointLetterSize].height,
            width:
              capWidth && videoMap[breakpointLetterSize].width - extraPadding > capWidth
                ? capWidth
                : videoMap[breakpointLetterSize].width - extraPadding,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedClient, breakpointLetterSize, extraPadding])

  useEffect(() => {
    if (source) {
      dispatch({
        type: 'initVideo',
        payload: {
          typeVideo: TYPES_SOURCES_VIDEOS.youtube,
        },
      })
    }
  }, [source])

  const handleEvent = (eventname) => {
    switch (eventname) {
      case 'playing':
        return fireAndForget({
          ...trackOnPlay,
          list_name: 'Video Player Play Initiated',
          click_id: 'Video Player Play Initiated',
          track_event: 'ui_click',
          event_action: 'ui_click',
          event_type: 'ui_click',
          ...event,
        })
      case 'pause':
        return fireAndForget({
          ...trackOnPlay,
          list_name: 'Video Player Play Initiated',
          click_id: 'Video Player Play Initiated',
          track_event: 'ui_click',
          event_action: 'ui_click',
          event_type: 'ui_click',
          ...event,
        })
      case 'end':
        return fireAndForget({
          ...trackOnPlay,
          list_name: 'Video Player Play Initiated',
          click_id: 'Video Player Play Initiated',
          track_event: 'ui_click',
          event_action: 'ui_click',
          event_type: 'ui_click',
          ...event,
        })
      default:
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleEvent)

    return () => {
      window.removeEventListener('message', handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const opts = {
    height: !frameEmbed && state.dimension.height,
    width: !frameEmbed && (isMobile ? mobileWidth : state.dimension.width),
    playerVars: {
      playsinline: 0,
      autoplay: autoplay ? 1 : 0,
      mute: isMobile ? 1 : 0,
    },
  }

  const autoOpts = {
    height: !frameEmbed && state.dimension.height,
    width: !frameEmbed && (isMobile ? mobileWidth : state.dimension.width),
    playerVars: {
      playsinline: 0,
      autoplay: 1,
      mute: isMobile ? 1 : 0,
    },
  }

  let paddingBottom
  if (aspectRatio) paddingBottom = `${(1 / aspectRatio) * 100}%`

  const ref = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize({ width, height })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Grid
        ref={ref}
        data-testid="iframe-container"
        style={
          frameClassName
            ? {}
            : {
                height:
                  state.typeVideo === TYPES_SOURCES_VIDEOS.youtube
                    ? 'fit-content'
                    : aspectRatio
                    ? heightAspectRadio
                    : state.dimension.height,
                width: capWidth
                  ? state.dimension.width
                  : isMobile
                  ? mobileWidth
                  : state.dimension.width,
                margin: 'auto',
              }
        }
        className={frameClassName ? `w-full ${frameClassName}` : frameClassName}
        container
      >
        <div
          data-testid="iframe-container"
          className={frameClassName ? 'w-full h-fit' : 'flex w-screen justify-center h-fit mb-4'}
        >
          {!state.showVideo && (
            <AchieveSkeleton
              variant="rectangular"
              width="100%"
              height={
                capWidth
                  ? state.dimension.height > capWidth / aspectRatio
                    ? capWidth / aspectRatio
                    : state.dimension.height
                  : state.typeVideo === TYPES_SOURCES_VIDEOS.youtube
                  ? 'fit-content'
                  : aspectRatio
                  ? heightAspectRadio
                  : state.dimension.height
              }
            />
          )}
          {state.showVideo ? (
            <>
              {state.typeVideo === TYPES_SOURCES_VIDEOS.youtube && (
                <>
                  {!thumbnail ? (
                    <VideoFrame
                      frameEmbed={frameEmbed}
                      source={source}
                      altText={altText}
                      opts={opts}
                      handleEvent={handleEvent}
                    />
                  ) : !state.clickToPlay ? (
                    <div
                      className={'relative w-full overflow-hidden'}
                      onClick={() => {
                        dispatch({
                          type: 'initVideo',
                          payload: {
                            clickToPlay: true,
                          },
                        })
                      }}
                    >
                      <div
                        className={
                          'inline-block absolute -translate-x-2/4 -translate-y-2/4 z-1 cursor-pointer w-16 h-16 left-2/4 top-2/4 lg:w-20 lg:h-20'
                        }
                        data-testid="play-button"
                      >
                        <PlayBtn title={'Play Button'} height="100%" width="100%" />
                      </div>
                      <MediaImageStandard
                        alt={altText}
                        content={thumbnail}
                        data-testid="video-thumbnail"
                        aspectRatio={aspectRatio}
                        fetchPriority="high"
                        preload={priority}
                      />
                    </div>
                  ) : (
                    <div
                      className={'relative w-full'}
                      style={{ width: '100%', height: 0, paddingBottom }}
                    >
                      {/* width 0 skeleton prevents cls*/}
                      <AchieveSkeleton
                        variant="rectangular"
                        width={0}
                        height={aspectRatio ? heightAspectRadio : state.dimension.height}
                      />
                      <div
                        style={{
                          marginTop: aspectRatio ? -heightAspectRadio : -state.dimension.height,
                        }}
                      >
                        <VideoFrame
                          frameEmbed={frameEmbed}
                          source={source}
                          altText={altText}
                          opts={
                            capWidth
                              ? { ...autoOpts, height: size.height, width: size.width }
                              : autoOpts
                          }
                          handleEvent={handleEvent}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : null}
          <HiddenIframe url={source} />
        </div>
      </Grid>
    </>
  )
}
export { VideoPlayer }
